/** Shared navigation config for the sidebar + topbar chrome.
 *
 * The sidebar mirrors the approved design: a top (unlabeled) group of primary
 * destinations, then grouped sections. Items without a real route are rendered
 * as disabled placeholders (`disabled: true`). */

import {
  BarChart3,
  Bus,
  CalendarRange,
  ClipboardList,
  ListChecks,
  Settings,
  Link,
  Smartphone,
  Tablet,
  Tags,
  UserCog,
  Users,
  Wrench,
  type LucideIcon,
} from "lucide-react";

import {
  ADMIN_PAGE_PERMISSION,
  PortalPermission,
} from "@/features/auth/access-config";

export interface NavLeaf {
  /** Route path; ignored when `disabled`. For `external` leaves, holds the
   * off-app URL instead of an in-app route. */
  to: string;
  label: string;
  icon: LucideIcon;
  /** Placeholder item with no backing route. */
  disabled?: boolean;
  /** Match the route exactly (used for the index route "/"). */
  end?: boolean;
  /** When set, the leaf is hidden unless the user holds this permission (`can()`). */
  requiredPermission?: PortalPermission;
  /** Renders as an `<a target="_blank">` to an off-app URL instead of an
   * in-app `<NavLink>`. Not gated by `requiredPermission` unless one is set. */
  external?: boolean;
}

export interface NavSection {
  /** Section heading; omit for the top, unlabeled group. */
  label?: string;
  items: readonly NavLeaf[];
}

/** The Driver App URL is env-configured (differs per deploy target). When
 * unset (e.g. a local/preview env without one configured), the nav renders a
 * disabled placeholder instead of a broken link. */
const DRIVER_APP_URL = import.meta.env.VITE_DRIVER_APP_URL;

const driverAppLeaf: NavLeaf = DRIVER_APP_URL
  ? {
      to: DRIVER_APP_URL,
      label: "Driver App",
      icon: Smartphone,
      external: true,
    }
  : {
      to: "#",
      label: "Driver App",
      icon: Smartphone,
      disabled: true,
    };

export const NAV_SECTIONS: readonly NavSection[] = [
  {
    items: [
      {
        to: "/planner",
        label: "Planner",
        icon: CalendarRange,
        requiredPermission: PortalPermission.PlannerView,
        end: true,
      },
      {
        to: "/planner/mobile-devices",
        label: "iPad Assignment",
        icon: Tablet,
        requiredPermission: PortalPermission.PlannerView,
      },
      {
        to: "/maintenance",
        label: "Maintenance",
        icon: Wrench,
        requiredPermission: PortalPermission.MaintenanceView,
      },
    ],
  },
  {
    label: "Management",
    items: [
      {
        to: "/planner/tags",
        label: "Tags",
        icon: Tags,
        requiredPermission: PortalPermission.PlannerManageTags,
      },
      { to: "#", label: "Activities", icon: ListChecks, disabled: true },
      { to: "#", label: "Vehicles", icon: Bus, disabled: true },
      { to: "#", label: "Manifest", icon: ClipboardList, disabled: true },
      { to: "#", label: "Assignments", icon: Users, disabled: true },
    ],
  },
  {
    label: "Reports",
    items: [
      {
        to: "/reports/bookings-date-range",
        label: "Banff to YYC PAX",
        icon: BarChart3,
        requiredPermission: PortalPermission.ReportView,
      },
    ],
  },
  {
    label: "Administration",
    items: [
      {
        to: "/users",
        label: "Users",
        icon: UserCog,
        requiredPermission: ADMIN_PAGE_PERMISSION,
      },
      {
        to: "/roles",
        label: "Roles & Permissions",
        icon: Users,
        requiredPermission: ADMIN_PAGE_PERMISSION,
      },
      { to: "#", label: "Settings", icon: Settings, disabled: true },
    ],
  },
  {
    label: "Tools",
    items: [
      {
        to: "/tracker-links",
        label: "Tracker link lookup",
        icon: Link,
        requiredPermission: PortalPermission.GenerateTrackerLink,
      },
    ],
  },
  {
    label: "Apps",
    items: [driverAppLeaf],
  },
];

export interface RouteMeta {
  /** Topbar page title. */
  title: string;
  /** Topbar breadcrumb (without the leading slash separator). */
  crumb?: string;
}

const ROUTE_META: ReadonlyArray<readonly [string, RouteMeta]> = [
  [
    "/planner/mobile-devices",
    { title: "iPad Assignment", crumb: "Assign iPads by date" },
  ],
  ["/planner/tags", { title: "Tags", crumb: "Manage the tag catalog" }],
  ["/planner", { title: "Planner", crumb: "Daily dispatch board" }],
  ["/maintenance", { title: "Maintenance", crumb: "Fleet calendar" }],
  ["/reports/bookings-date-range", { title: "Banff to YYC PAX" }],
  ["/reports", { title: "Reports" }],
  ["/users", { title: "Users", crumb: "Manage staff accounts" }],
  ["/roles", { title: "Roles & Permissions", crumb: "Manage role access" }],
  ["/tracker-links", { title: "Tracker link lookup" }],
];

/** Resolve the topbar title/breadcrumb for the current pathname. */
export function getRouteMeta(pathname: string): RouteMeta {
  // Longest matching prefix wins; "/" only matches exactly.
  const exact = ROUTE_META.find(([path]) => path === pathname);
  if (exact) return exact[1];
  const prefix = ROUTE_META.find(
    ([path]) => path !== "/" && pathname.startsWith(`${path}/`),
  );
  if (prefix) return prefix[1];
  return { title: "Banff Airporter" };
}
